import React from 'react';

interface PositionSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPosition: (position: 'start' | 'end' | number) => void;
  tierName: string;
  tierColor: string;
  itemCount: number;
  currentPosition: number;
  position: { x: number, y: number };
}

/**
 * A popup menu for selecting where to position an item within a tier
 */
export default function PositionSelector({
  isOpen,
  onClose,
  onSelectPosition,
  tierName,
  tierColor,
  itemCount,
  currentPosition,
  position
}: PositionSelectorProps) {
  if (!isOpen) return null;

  // Close when clicking outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      // console.log('[PositionSelector] Backdrop clicked, closing');
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  // Handle position selection
  const handleSelectPosition = (e: React.MouseEvent, selectedPosition: 'start' | 'end' | number) => {
    // console.log('[PositionSelector] Position selected:', selectedPosition);
    e.preventDefault();
    e.stopPropagation();
    
    // If it's a number, adjust to be 0-indexed since we're displaying 1-indexed to the user
    if (typeof selectedPosition === 'number') {
      // Convert 1-indexed display to 0-indexed internal position
      selectedPosition = selectedPosition - 1;
    }
    
    onSelectPosition(selectedPosition);
  };

  // Calculate position, keeping the popup within viewport
  const posX = Math.min(position.x, window.innerWidth - 200);
  const posY = Math.min(position.y, window.innerHeight - 200);

  // Generate numbered position options (don't include current position)
  const positionOptions = [];
  for (let i = 0; i < itemCount; i++) {
    if (i !== currentPosition) {
      // Store as 1-indexed display value
      positionOptions.push(i + 1);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      <div
        className="absolute bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden"
        style={{
          left: `${posX}px`,
          top: `${posY}px`,
          width: '200px',
          maxWidth: '90vw',
        }}
        onClick={(e) => e.stopPropagation()} // Prevent clicks on the container from bubbling
      >
        <div
          className="p-3 border-b border-gray-700"
          style={{ backgroundColor: tierColor }}
        >
          <h3 className="text-sm font-medium text-white">Reorder in {tierName} Tier:</h3>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-700 text-gray-200"
            onClick={(e) => handleSelectPosition(e, 'start')}
          >
            Move to Start
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-700 text-gray-200"
            onClick={(e) => handleSelectPosition(e, 'end')}
          >
            Move to End
          </button>

          {/* Only show custom positions if there are more than 2 items */}
          {itemCount > 2 && (
            <>
              <div className="px-4 py-2 text-xs text-gray-400 border-t border-gray-700">
                Custom Position:
              </div>
              <div className="flex flex-wrap p-2 gap-2">
                {positionOptions.map((pos) => (
                  <button
                    key={`position-${pos}`}
                    className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-sm text-gray-200"
                    onClick={(e) => handleSelectPosition(e, pos)}
                  >
                    {pos}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
} 